import { Date_toDateString, Date_toDateTimeString } from "../../../common/dateUtils";
import { FormState_PopulateErrorMessages } from "../../../common/formStateUtils";
import { String_removePrefix } from "../../../common/stringUtils";
import ShipRepairsPermitRequest from "../../requests/ShipRepairsPermitRequest";
import Errors from "../../responses/Errors";
import FormElement, { formElement } from "../FormElement";
import PersonFormElement, {
    PersonFormElement_default,
    PersonFormElement_toPerson,
    PersonFormElement_populateErrorMessages
} from "../PersonFormElement";
import PositionPickerRow, {
    PositionPickerRow_default,
    PositionPickerRow_toPosition,
    PositionPickerRows_populateErrorMessages
} from "./PositionPickerRow";

type ShipRepairsPermitFormState = {
    remindMe: FormElement<boolean>;
    remindMeDate: FormElement<Date>;
    startDate: FormElement<Date>;
    endDate: FormElement<Date>;
    adminComment: FormElement<string>;
    description: FormElement<string>;
    expirationReminderEmail: FormElement<string>;
    expirationReminderPhoneNumber: FormElement<string>;
    shipName: FormElement<string>;
    callSign: FormElement<string>;
    commanderOrRepresentative: PersonFormElement;
    includesMajorRepairs: FormElement<boolean>;
    shipCanBeMovedByOwnMachine: FormElement<boolean>;
    includesRunningPropellers: FormElement<boolean>;
    shipCanBeMovedByTowBoat: FormElement<boolean>;
    hasAgreementWithTowBoatCompany: FormElement<boolean>;
    positions: PositionPickerRow[];
    anonymousUserEmail: FormElement<string>;
};

export default ShipRepairsPermitFormState;

export function ShipRepairsPermitFormState_default(): ShipRepairsPermitFormState {
    const shipRepairsPermitFormState: ShipRepairsPermitFormState = {
        adminComment: formElement(""),
        commanderOrRepresentative: PersonFormElement_default(),
        callSign: formElement(""),
        description: formElement(""),
        endDate: formElement(new Date()),
        expirationReminderEmail: formElement(""),
        expirationReminderPhoneNumber: formElement(""),
        hasAgreementWithTowBoatCompany: formElement(false),
        includesMajorRepairs: formElement(false),
        includesRunningPropellers: formElement(false),
        positions: [PositionPickerRow_default()],
        remindMe: formElement(false),
        remindMeDate: formElement(new Date()),
        shipCanBeMovedByOwnMachine: formElement(false),
        shipCanBeMovedByTowBoat: formElement(false),
        shipName: formElement(""),
        startDate: formElement(new Date()),
        anonymousUserEmail: formElement("")
    };

    return shipRepairsPermitFormState;
}

export function ShipRepairsPermitFormState_toRequest(formState: ShipRepairsPermitFormState): ShipRepairsPermitRequest {
    const request: ShipRepairsPermitRequest = {
        adminComment: formState.adminComment.value,
        callSign: formState.callSign.value,
        commanderOrRepresentative: PersonFormElement_toPerson(formState.commanderOrRepresentative),
        description: formState.description.value,
        endDate: Date_toDateTimeString(formState.endDate.value),
        expirationReminderEmail: formState.expirationReminderEmail.value,
        expirationReminderPhoneNumber: formState.expirationReminderPhoneNumber.value,
        hasAgreementWithTowBoatCompany: formState.hasAgreementWithTowBoatCompany.value,
        includesMajorRepairs: formState.includesMajorRepairs.value,
        includesRunningPropellers: formState.includesRunningPropellers.value,
        positions: formState.positions.map(PositionPickerRow_toPosition),
        remindMe: formState.remindMe.value,
        remindMeDate: Date_toDateString(formState.remindMeDate.value),
        shipCanBeMovedByOwnMachine: formState.shipCanBeMovedByOwnMachine.value,
        shipCanBeMovedByTowBoat: formState.shipCanBeMovedByTowBoat.value,
        shipName: formState.shipName.value,
        startDate: Date_toDateTimeString(formState.startDate.value),
        anonymousUserEmail: formState.anonymousUserEmail.value
    };

    return request;
}

export function ShipRepairsPermitFormState_populateErrorMessages(
    formState: ShipRepairsPermitFormState,
    errors: Errors
): ShipRepairsPermitFormState {
    const errorKeys = Object.keys(errors);

    const positionsErrorKeys = errorKeys.filter((e) => e.startsWith("Positions"));
    const positionErrors = Object.fromEntries(
        positionsErrorKeys.map((k) => [String_removePrefix(k, "Positions"), errors[k]])
    );

    const commanderOrRepresentativeErrorKeys = errorKeys.filter((e) => e.startsWith("CommanderOrRepresentative."));
    const commanderOrRepresentativeErrors = Object.fromEntries(
        commanderOrRepresentativeErrorKeys.map((k) => [String_removePrefix(k, "CommanderOrRepresentative."), errors[k]])
    );

    const updatedFormState = FormState_PopulateErrorMessages(formState, errors);
    updatedFormState.positions = PositionPickerRows_populateErrorMessages(formState.positions, positionErrors);

    updatedFormState.commanderOrRepresentative = PersonFormElement_populateErrorMessages(
        formState.commanderOrRepresentative,
        commanderOrRepresentativeErrors
    );

    return updatedFormState;
}
